import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../../context/AuthContext";
import { fetchOrders } from "./api/ordersApi";

const normalizeNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeOrder = (order) => ({
  ...order,
  selling_price: normalizeNumber(order.selling_price),
  total_product_cost: normalizeNumber(order.total_product_cost ?? order.product_cost),
  total_shipping_cost: normalizeNumber(order.total_shipping_cost ?? order.shipping_cost),
  total_allegro_fee: normalizeNumber(order.total_allegro_fee ?? order.allegro_fee),
  total_other_costs: normalizeNumber(order.total_other_costs ?? order.other_costs),
  net_profit: normalizeNumber(order.net_profit),
  total_costs: normalizeNumber(order.total_costs),
  profit_margin: normalizeNumber(order.profit_margin),
});

export const useOrdersData = () => {
  const { authToken, isAuthenticated, isEmailVerified } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canLoad = useMemo(
    () => Boolean(isAuthenticated && authToken && isEmailVerified),
    [authToken, isAuthenticated, isEmailVerified]
  );

  const loadOrders = useCallback(async () => {
    if (!canLoad) {
      setOrders([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrders(authToken);
      const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      setOrders(list.map(normalizeOrder));
    } catch (err) {
      console.error("Error while fetching orders:", err);
      setError(err.message || "Unable to load orders.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [authToken, canLoad]);

  useEffect(() => {
    if (!canLoad) {
      setOrders([]);
      setError(null);
      setLoading(false);
      return;
    }
    loadOrders();
  }, [canLoad, loadOrders]);

  return {
    orders,
    loading,
    error,
    reload: loadOrders,
  };
};
